import React, { useEffect, useRef } from "react";
import { ChevronRightIcon, SearchIcon } from "../../icons";
import AssetFilter from "./AssetFilter";
import DateRangeFilter from "./DateRangeFilter";
import TransactionListItem from "./TransactionListItem";
import TransactionTypeFilter from "./TransactionTypeFilter";
import { TransactionListSkeleton } from "../../../components/Skeleton/TransactionHistorySkeleton";
import EmptyNewCryptoTransaction from "../../../components/Empty/EmptyNewCryptoTransaction";

const TransactionListPanel = ({
  transactions = [],
  selected,
  searchQuery,
  typeFilter,
  selectedAssets,
  cryptoAccounts,
  dateRange,
  onSearchChange,
  onTypeFilterChange,
  onAssetFilterChange,
  onDateRangeChange,
  onSelect,
  onPageBack,
  onLoadMore,
  hasMore = false,
  loadMore = false,
  loadingTransactions
}) => {
  const loadMoreRef = useRef(null);

  // Infinite scroll listener using IntersectionObserver
  useEffect(() => {
    if (!onLoadMore || !hasMore || loadMore) return;

    const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
    }, { threshold: 0.5 });

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [onLoadMore, hasMore, loadMore]);

  return (
    <div className="flex w-full flex-col gap-4 overflow-visible lg:w-[556px] lg:shrink-0 lg:gap-5">
      {/* Top Header & Desktop Search/Filters Layout */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
        
        {/* Title Bar: Centered on Mobile with Back Button, Plain Text on Desktop */}
        <div className="relative flex items-center justify-center lg:block lg:justify-start">
          <button
            type="button"
            onClick={onPageBack}
            className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full p-0 custom-transition hover:bg-option-hover lg:hidden"
            aria-label="Go back"
          >
            <ChevronRightIcon className="rotate-180 text-text-secondary" />
          </button>
          
          <h1 className="text-base font-semibold leading-7 text-text-primary lg:text-[20px] lg:leading-[28px]">
            Transaction History
          </h1>
        </div>

        {/* Filters Group: Mobile (Below search) | Desktop (Top right) */}
        <div className="hidden lg:flex lg:shrink-0 lg:items-center lg:gap-2">
          <AssetFilter
            cryptoAccounts={cryptoAccounts}
            selectedAssets={selectedAssets}
            onChange={onAssetFilterChange}
          />
          <TransactionTypeFilter
            value={typeFilter}
            onChange={onTypeFilterChange}
          />
          <DateRangeFilter value={dateRange} onChange={onDateRangeChange} />
        </div>
      </div>

      {/* Search Input Bar */}
      <div className="flex h-10 w-full items-center gap-2 rounded-[10px] border border-divider bg-background-surface py-2.5 pl-4 pr-3 lg:h-12 lg:rounded-[8px] lg:border-solid">
        <SearchIcon />
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search Transactions"
          className="w-full border-0 bg-transparent p-0 text-xs font-normal text-text-primary placeholder:text-text-secondary outline-none focus:ring-0"
        />
      </div>

      {/* Mobile-Only Filters Row */}
      <div className="relative flex shrink-0 items-center gap-2 lg:hidden">
        <AssetFilter
          cryptoAccounts={cryptoAccounts}
          selectedAssets={selectedAssets}
          onChange={onAssetFilterChange}
        />
        <TransactionTypeFilter
          value={typeFilter}
          onChange={onTypeFilterChange}
        />
        <DateRangeFilter value={dateRange} onChange={onDateRangeChange} />
      </div>

      {loadingTransactions ? 
        <TransactionListSkeleton />
        :
        <>
          {/* Transactions List Section */}
          <div className="relative z-0 flex w-full flex-col gap-1 lg:max-h-[640px] lg:h-[640px]">
            {/* <p className="text-sm font-medium leading-7 text-text-primary">Recent</p> */}
            
            <div className="flex flex-col gap-2 lg:max-h-[640px] lg:overflow-y-auto hide-scrollbar">
              {transactions.length === 0 ? (
                <EmptyNewCryptoTransaction />
              ) : (
                <>
                  {transactions.map((transaction) => {
                    const trnId = transaction.transaction_id || transaction.id;
                    const isSelected = selected?.transaction_id === trnId || selected?.id === trnId;

                    return (
                      <TransactionListItem
                        key={trnId}
                        transaction={transaction}
                        isSelected={isSelected}
                        onSelect={onSelect}
                      />
                    );
                  })}

                  {/* Load More Trigger Sentinel */}
                  {hasMore && (
                    <div ref={loadMoreRef} className="py-3 text-center">
                      {loadMore ? (
                        <span className="text-xs text-text-secondary animate-pulse">
                          Loading more transactions...
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={onLoadMore}
                          className="text-xs font-medium text-text-secondary hover:text-text-primary"
                        >
                          Load More
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      }
        
    </div>
  );
};

export default TransactionListPanel;